import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Download } from "lucide-react";
import CTABanner from "../components/CTABanner";
import StarMotif from "../components/StarMotif";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bullets = ({ items, testid }) => (
  <ul className="space-y-2.5 mt-3" data-testid={testid}>
    {(items || []).map((t, i) => (
      <li key={i} className="flex items-start gap-2.5 ek-body text-[15px]">
        <Check size={17} strokeWidth={2} className="mt-0.5 flex-none text-[#7737F6]" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ title, children }) => (
  <div className="mt-9">
    <h2 className="ek-heading text-2xl text-black">{title}</h2>
    {children}
  </div>
);

const CaseStudy = () => {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    axios.get(`${API}/content/case-studies/${slug}`)
      .then((res) => { if (alive) { setCs(res.data); setState("ok"); } })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; };
  }, [slug]);

  if (state === "loading") {
    return <div className="ek-container py-32"><div className="ek-card animate-pulse h-64 bg-white" /></div>;
  }
  if (state === "error" || !cs) {
    return (
      <div className="ek-container py-32 text-center" data-testid="case-study-not-found">
        <h1 className="ek-heading text-3xl text-black">Case study not found</h1>
        <Link to="/case-studies" className="inline-flex items-center gap-2 text-[#7737F6] mt-6 font-medium"><ArrowLeft size={16} /> All case studies</Link>
      </div>
    );
  }

  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-16 md:py-24">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm mb-8" data-testid="case-study-back">
            <ArrowLeft size={15} /> All case studies
          </Link>
          <div className="ek-eyebrow text-[#A179FA]">{cs.sector}</div>
          <h1 className="ek-heading text-3xl md:text-4xl lg:text-5xl mt-4 text-white max-w-4xl leading-tight" data-testid="case-study-title">
            {cs.title}
          </h1>
          <a
            href={`${API}/content/case-studies/${cs.slug}/pdf`}
            className="inline-flex items-center gap-2 mt-7 bg-white text-[#0B0B0C] font-medium text-sm px-5 py-2.5 rounded-full hover:bg-zinc-100 transition-colors"
            data-testid="case-study-download"
          >
            <Download size={16} /> Download case study (PDF)
          </a>
          <div className="mt-10 flex flex-wrap items-end gap-x-10 gap-y-6">
            <div>
              <div className="ek-heading text-5xl md:text-6xl text-white leading-none" data-testid="case-study-metric">{cs.metric_value}</div>
              <div className="text-[13px] text-zinc-300 mt-2 max-w-xs">{cs.metric_label}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(cs.services_tags || []).map((t) => (
                <span key={t} className="text-[11px] font-medium text-white/85 border border-white/20 px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white ek-section">
        <div className="ek-container max-w-4xl">
          <div className="flex items-center gap-3">
            <StarMotif size={20} color="#7737F6" />
            <span className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase">Ekselens case study</span>
          </div>

          <Block title="The business situation"><p className="ek-body text-[15px] mt-3 leading-relaxed">{cs.business_situation}</p></Block>
          {cs.why_complex?.length > 0 && <Block title="Why it was complex"><Bullets items={cs.why_complex} testid="cs-why-complex" /></Block>}
          <Block title="What changed"><p className="ek-body text-[15px] mt-3 leading-relaxed">{cs.what_changed}</p></Block>
          <Block title="Commercial and operational impact"><p className="ek-body text-[15px] mt-3 leading-relaxed">{cs.impact}</p></Block>
          {cs.delivery_moves?.length > 0 && <Block title="Delivery moves"><Bullets items={cs.delivery_moves} testid="cs-delivery-moves" /></Block>}
          {cs.evidence?.length > 0 && <Block title="Evidence of value"><Bullets items={cs.evidence} testid="cs-evidence" /></Block>}

          {cs.what_this_proves && (
            <div className="mt-10 bg-[#0B0B0C] text-white p-7 md:p-9" data-testid="cs-what-this-proves">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-[#A179FA] uppercase">What this proves</div>
              <p className="ek-heading text-xl md:text-2xl mt-3 leading-snug">{cs.what_this_proves}</p>
            </div>
          )}

          {cs.relevant_services?.length > 0 && (
            <Block title="Relevant Ekselens services">
              <div className="flex flex-wrap gap-2 mt-4">
                {cs.relevant_services.map((s) => (
                  <span key={s} className="text-[12.5px] text-zinc-700 bg-zinc-100 px-3 py-1.5 rounded-full">{s}</span>
                ))}
              </div>
            </Block>
          )}

          {cs.key_takeaway && (
            <div className="mt-10 border-l-4 border-[#7737F6] pl-5 py-1">
              <div className="text-[11px] font-semibold tracking-[0.16em] text-[#7737F6] uppercase">Key takeaway</div>
              <p className="ek-body text-[15px] mt-1.5 italic">{cs.key_takeaway}</p>
            </div>
          )}

          <div className="mt-12">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-[#7737F6] font-medium">
              More case studies <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default CaseStudy;
